import { ChangeEvent, useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import toast from 'react-hot-toast';

import { apiPaths } from '../../../../../service/apiPaths';
import ApiService from '../../../../../service/ApiService';
import { IAddTaskSheet, ITaskSheet } from '../../Tasksheet.props';

const useEditTaskSheet = (taskSheetId: string, setMethod: () => void) => {
  const today = new Date().toISOString().split("T")[0];

  const [formData, setFormData] = useState<IAddTaskSheet>({
    description: "",
    priority: "low",
    assignedTo: "",
    dueDate: today,
    status: "todo",
    projectId: ""
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [taskSheet, setTaskSheet] = useState<ITaskSheet | null>(null);
  const [user, setUser] = useState<any[]>([]);
  const [project, setProject] = useState<any[]>([]);

  useEffect(() => {
    fetchData();
    fetchUserData();
    fetchProjectData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.getAllTaskSheets}?id=${taskSheetId}`,
      });
      
      const candidate = (response && response.data) ? response.data : response;

      let task: any = null;
      let derivedAssignedToId: string | undefined;
      let derivedProjectId: string | undefined;

      if (candidate && candidate.employees && Array.isArray(candidate.employees)) {
        const employee = candidate.employees[0];
        const projectData = employee?.projects?.[0];
        const tasksArray = projectData?.tasks || [];
        task = tasksArray.find((t: any) => t?._id === taskSheetId) || tasksArray[0] || null;
        derivedAssignedToId = employee?.employee?._id;
        derivedProjectId = projectData?.project?._id;
        if (task) {
          task.assignedTo = task.assignedTo || derivedAssignedToId;
          task.projectId = task.projectId || derivedProjectId;
        }
      } else if (Array.isArray(candidate)) {
        task = candidate.find((t: any) => t?._id === taskSheetId) || candidate[0] || null;
      } else {
        task = candidate;
      }

      if (!task) {
        throw new Error("Task not found");
      }

      setTaskSheet(task);
      populateFormData(task);
    } catch (error) {
      toast.error(`Error - ${error}`, {
        duration: 5000,
      });
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectData = async () => {
    setLoading(true);
    try {
      const result = await ApiService({
        method: 'GET',
        endpoint: `${apiPaths.getProjects}`,
      });
      setProject(result);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const result = await ApiService({
        method: 'GET',
        endpoint: `${apiPaths.getEmployees}`,
      });
      setUser(result);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const populateFormData = (taskSheetData: any) => {
    const assignedToId = typeof taskSheetData?.assignedTo === "string"
      ? taskSheetData.assignedTo
      : taskSheetData?.assignedTo?._id || "";
    const projectIdVal = typeof taskSheetData?.projectId === "string"
      ? taskSheetData.projectId
      : taskSheetData?.projectId?._id || "";

    setFormData({
      description: taskSheetData?.description ?? "",
      priority: taskSheetData?.priority ?? "low",
      assignedTo: assignedToId,
      dueDate: taskSheetData?.dueDate ?? today,
      status: taskSheetData?.status ?? "todo",
      projectId: projectIdVal
    });
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement;
    const key = (target as any).id || (target as any).name;
    const value = target.value;
    if (!key) return;
    setFormData({ ...formData, [key]: value });
  };

  // New handler for priority changes
  const handlePriorityChange = (newPriority: string) => {
    setFormData({ ...formData, priority: newPriority });
  };

  // New handler for status changes
  const handleStatusChange = (newStatus: string) => {
    setFormData({ ...formData, status: newStatus });
  };

  // New handler for user/assignedTo changes
  const handleUserChange = (newUserId: string) => {
    setFormData({ ...formData, assignedTo: newUserId });
  };

  // New handler for project changes
  const handleProjectChange = (newProjectId: string) => {
    setFormData({ ...formData, projectId: newProjectId });
  };

  const handleSubmit: SubmitHandler<IAddTaskSheet> = async () => {
    const data = {
      description: formData.description,
      priority: formData.priority,
      assignedTo: formData.assignedTo,
      dueDate: formData.dueDate,
      status: formData.status,
      projectId: formData.projectId
    };

    console.log("Submitting data:", data);

    try {
      const response = await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.updateTaskSheet}${taskSheetId}`,
        data: data,
      });
      if (response !== "undefined") {
        toast.success("TaskSheet updated successfully", {
          duration: 5000,
        });
        setTimeout(() => {
          setMethod();
        }, 1500);
      }
    } catch (error) {
      toast.error(`Error - ${error}`, {
        duration: 5000,
      });
      console.error("Error updating taskSheet:", error);
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    formData,
    taskSheet,
    handleChange,
    handleSubmit,
    handlePriorityChange,
    handleStatusChange,
    handleUserChange,
    handleProjectChange,
    user,
    project,
    setFormData
  };
};

export default useEditTaskSheet;